import React, { useEffect } from 'react';
import { Sidebar } from './components/Sidebar';
import { LoadingView } from './components/LoadingView';
import { ErrorView } from './components/ErrorView';
import { EmptyView } from './components/EmptyView';
import { DailyDataView } from './components/DailyDataView';
import { PeriodDataView } from './components/PeriodDataView';
import { UserSearchDataView } from './components/UserSearchDataView';
import { SearchForm, DailySearchForm, UserSearchForm } from './types';
import { useSleepData } from './hooks/useSleepData';
import { useSearchForm } from './hooks/useSearchForm';
import { useMetricSelection } from './hooks/useMetricSelection';

export const App: React.FC = () => {
  const { 
    chartData, 
    dailyData,
    userSearchResults,
    userSearchParams, 
    viewType, 
    isLoading, 
    error, 
    handlePeriodSearch, 
    handleDailySearch,
    handleUserSearch,
    setError 
  } = useSleepData();

  const { 
    updatePeriodParams, 
    updateDailyParams,
    updateUserSearchParams, 
    getFormFromUrl 
  } = useSearchForm();

  const { 
    selectedMetrics, 
    handleMetricToggle 
  } = useMetricSelection();

  // 初回レンダリング時にURLパラメータから検索を実行
  useEffect(() => {
    const { periodForm, dailyForm, userSearchForm } = getFormFromUrl();
    
    if (periodForm) {
      handlePeriodSearch(periodForm);
    } else if (dailyForm) {
      handleDailySearch(dailyForm);
    } else if (userSearchForm) {
      handleUserSearch(userSearchForm);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 検索ハンドラ（URLパラメータ更新含む）
  const onPeriodSearch = async (form: SearchForm) => {
    updatePeriodParams(form);
    await handlePeriodSearch(form);
  };

  const onDailySearch = async (form: DailySearchForm) => {
    updateDailyParams(form);
    await handleDailySearch(form);
  };

  const onUserSearch = async (form: UserSearchForm) => {
    updateUserSearchParams(form);
    await handleUserSearch(form);
  };

  // コンテンツ表示
  const renderContent = () => {
    if (isLoading) {
      return <LoadingView />;
    }

    if (error) {
      return <ErrorView error={error} onRetry={() => setError(null)} />;
    }

    if (viewType === 'empty') {
      return <EmptyView />;
    }

    if (viewType === 'daily' && dailyData) {
      return <DailyDataView data={dailyData} />;
    }

    if (viewType === 'period' && chartData) {
      return (
        <PeriodDataView 
          data={chartData} 
          selectedMetrics={selectedMetrics} 
          onMetricToggle={handleMetricToggle} 
        />
      );
    }

    if (viewType === 'usersearch' && userSearchResults && userSearchParams) {
      return (
        <UserSearchDataView
          results={userSearchResults}
          searchParams={userSearchParams}
        />
      );
    }

    return null;
  };

  const { periodForm: initialPeriodForm, dailyForm: initialDailyForm, userSearchForm: initialUserSearchForm } = getFormFromUrl();

  return (
    <div className="h-screen flex bg-gray-50">
      <Sidebar 
        onPeriodSearch={onPeriodSearch} 
        onDailySearch={onDailySearch}
        onUserSearch={onUserSearch} 
        isLoading={isLoading}
        initialPeriodForm={initialPeriodForm}
        initialDailyForm={initialDailyForm}
        initialUserSearchForm={initialUserSearchForm}
      />
      {renderContent()}
    </div>
  );
};